import { Router } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { inject, Injectable, signal } from '@angular/core';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { catchError, filter, map, skipWhile, switchMap, take, tap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { DoodMarketplaceModel, ILayout } from '@core/models/marketplace.model';
import { MarketplaceKeys } from '@config/keys/shop.keys';

import { CartStoreSelector } from '@common/selectors/cart.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { MarketplaceStoreDispatcher } from '@common/dispatchers/marketplace.dispatcher';

import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { EventTrackerService } from '@core/services/event-tracker/event-tracker.service';
import { MarketplacesApiService } from '@core/services/api/marketplaces/marketplaces-api.service';
import { MarketplacesContentBuilderApiService } from '@core/services/api/marketplaces/marketplaces-content-builder-api.service';
import { ShopSearchParametersService } from '@core/services/shop-search-parameters/shop-search-parameters.service';

import { ContentStoreDispatcher } from '@common/dispatchers/content.dispatcher';
import { Auth } from '@angular/fire/auth';
import { OrderTypeService } from '../order-type/order-type.service';
import {
  checkEnvValidityOrReload,
  determineDomainEnvironmentFromDomain,
  extractDomainFromUrl,
} from '@app/utilities/domain.util';
import { ModalsService } from '../modals/modals.service';
import {
  BlockingAlertModalComponent,
  BlockingAlertModalConfig,
} from '@shared/modals/blocking-alert-modal/blocking-alert-modal.component';
import { MarketPlaceNotAuthorizedComponent } from '@shared/components/marketplace-error/marketplace-not-authorized.component';
import { MarketPlaceNotFoundComponent } from '@shared/components/marketplace-error/marketplace-not-found.component';
import { MarketPlacePluginTypeMap, MarketPlacePluginTypeMapper } from '@core/models/plugin.model';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { Cb2PreviewBootService } from '@core/services/cb2-preview/cb2-preview-boot.service';
import { Cb2PreviewMarketplaceApplierService } from '@core/services/cb2-preview/cb2-preview-marketplace-applier.service';
import { JourneyParametersRuntimeService } from '@core/services/content-builder/journey-parameters-runtime.service';
import {
  applyMarketplaceUserInterface,
  convertToPageTree,
  initializeDefaultDistributionMode,
  injectCb2DefaultPages,
  injectDefaultModals,
  injectDefaults,
  resolveAuthMode,
  setMarketplaceDefaultTimezone,
  setMarketplaceHtmlLang,
} from './marketplace-runtime.util';
import { createMarketplaceRoutes } from './marketplace-routes.util';
import { readContentBuilderSpecModeFromUrl } from '@core/services/cb2-preview/runtime/content-builder-spec-mode.util';
import { isContentBuilderV2Marketplace } from '@core/services/cb2-preview/runtime/content-builder-version.util';
import { Cb2ContextualRuntimeResolverService } from '@core/services/content-builder/cb2-contextual-runtime-resolver.service';

@Injectable({
  providedIn: 'root',
})
export class MarketplaceService {
  private auth: Auth = inject(Auth);
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly cb2PreviewBoot = inject(Cb2PreviewBootService);
  private readonly cb2PreviewApplier = inject(Cb2PreviewMarketplaceApplierService);
  private readonly marketplaceContentBuilderApiService = inject(
    MarketplacesContentBuilderApiService,
  );
  private readonly journeyParametersRuntime = inject(JourneyParametersRuntimeService);
  private readonly cb2ContextualRuntimeResolver = inject(Cb2ContextualRuntimeResolverService);

  static localStorageKey = 'marketplace_domain';
  readonly localizeRouterService = inject(LocalizeRouterService);
  readonly router = inject(Router);
  readonly routerHelperService = inject(RouterHelperService);

  constructor(
    private cartSelector: CartStoreSelector,
    private cartDispatcher: CartStoreDispatcher,
    private readonly translate: TranslateService,
    private contentDispatcher: ContentStoreDispatcher,
    private marketplaceSelector: MarketplaceStoreSelector,
    private marketplaceDispatcher: MarketplaceStoreDispatcher,
    private readonly eventTrackerService: EventTrackerService,
    private readonly marketplaceApiService: MarketplacesApiService,
    private readonly shopSearchParametersService: ShopSearchParametersService,
    private readonly orderTypeService: OrderTypeService,
    private readonly modalsService: ModalsService<BlockingAlertModalConfig>,
    private readonly analyticsService: AnalyticsService,
    private readonly shopSelector: ShopStoreSelector,
  ) {}

  public loadMarketplace$(): Observable<DoodMarketplaceModel> {
    // CB2 preview: skip the domain API entirely — the dashboard pushes a full
    // marketplace stub via postMessage and the applier hydrates the stores.
    if (this.cb2PreviewState.isActive()) {
      return this.cb2PreviewBoot.waitForInitialPayload().pipe(
        map(payload => this.cb2PreviewApplier.apply(payload)),
        filter((marketplace): marketplace is DoodMarketplaceModel => marketplace !== null),
        tap(marketplace => this.setFirebaseTenantId(marketplace)),
      );
    }

    const dataDomain = this.pickDomain();
    const domain = dataDomain.domain;
    this.saveDomainToLocalStorage(domain);
    if (!environment.production) {
      checkEnvValidityOrReload(dataDomain);
    }

    return this.marketplaceApiService.getMarketplaceByDomain$(domain).pipe(
      tap(marketplace => this.handleMarketplaceNotFound(marketplace)),
      skipWhile(marketplace => !marketplace),
      map(marketplace => marketplace as DoodMarketplaceModel),
      switchMap(marketplace => this.materializeMarketplaceRuntime$(marketplace)),
      tap(marketplace => this.loadMarketplaceData(marketplace)),
      tap(marketplace => setMarketplaceHtmlLang(marketplace)),
      tap(marketplace => this.injectCustomTranslation(marketplace)),
      tap(marketplace =>
        initializeDefaultDistributionMode(marketplace, {
          shopSearchParametersService: this.shopSearchParametersService,
          orderTypeService: this.orderTypeService,
        }),
      ),
      map(marketplace => convertToPageTree(marketplace)),
      // CB2 keeps its own pages. Missing functional routes come from
      // WEBAPP_CB2_DEFAULT_PAGE_ROUTES, merged like the v1 defaults.
      map(marketplace =>
        isContentBuilderV2Marketplace(marketplace)
          ? injectCb2DefaultPages(injectDefaultModals(marketplace))
          : injectDefaults(marketplace),
      ),
      tap(marketplace => this.loadBlocks(marketplace)),
      tap(marketplace => this.loadModals(marketplace)),
      tap(marketplace => applyMarketplaceUserInterface(marketplace)),
      tap(marketplace =>
        createMarketplaceRoutes(marketplace, {
          router: this.router,
          localizeRouterService: this.localizeRouterService,
          routerHelperService: this.routerHelperService,
        }),
      ),
      tap(marketplace => setMarketplaceDefaultTimezone(marketplace)),
      tap(marketplace => this.setAuthMode(marketplace)),
      tap(marketplace => this.dispatchTrackEvent(marketplace)),
      tap(marketplace => this.setFirebaseTenantId(marketplace)),
      switchMap(marketplace =>
        combineLatest([of(marketplace), this.cartSelector.selectActive$.pipe(take(1))]),
      ),
      tap(([marketplace, cart]) => {
        if (cart) {
          if (cart?.marketplace && cart.marketplace !== marketplace.id) {
            this.cartDispatcher.resetAndClear();
          }
        }
      }),
      map(([marketplace]) => marketplace),
      catchError(error => {
        if (error?.status === 404) {
          // not working because the modal is initialized before the store and will be removed
          this.modalsService.open(BlockingAlertModalComponent.handle, undefined, {
            componentRef: signal(MarketPlaceNotFoundComponent),
          });
        } else if (error?.status === 403) {
          this.modalsService.open(BlockingAlertModalComponent.handle, undefined, {
            componentRef: signal(MarketPlaceNotAuthorizedComponent),
          });
        }

        return []; // return an empty array to avoid breaking the observable chain (and the app will still display the initial loading)
      }),
    ) as Observable<DoodMarketplaceModel>;
  }

  handleMarketplaceNotFound(marketplace: DoodMarketplaceModel | undefined): void {
    if (marketplace) {
      return;
    }

    this.router.navigate(['404']);
  }

  public getLayout$(): Observable<ILayout | undefined> {
    return this.marketplaceSelector.selectMarketplaceLayout;
  }

  public getId$(): Observable<string> {
    return this.marketplaceSelector.selectMarketplaceId$;
  }

  public getId(): string {
    return this.marketplaceSelector.marketplace?.id;
  }
  /**
   * You may not need this method, you can retrieve the marketplace plugins directly from the selector.
   */
  public getPluginByType<const PluginKey extends keyof MarketPlacePluginTypeMap | (string & {})>(
    type: PluginKey,
  ): MarketPlacePluginTypeMapper<PluginKey> | undefined {
    const marketplace = this.marketplaceSelector.marketplace;

    return marketplace?.[MarketplaceKeys.Plugins]?.filter(plugin => plugin?.type === type)[0] as
      MarketPlacePluginTypeMapper<PluginKey> | undefined;
  }

  public getFirebaseTenantId(): string | null | undefined {
    const marketplace = this.getMarketplaceData();
    if (
      marketplace[MarketplaceKeys.FirebaseTenant] !== undefined &&
      marketplace[MarketplaceKeys.FirebaseTenant]?.firebase_tenant_id !== undefined
    ) {
      return marketplace[MarketplaceKeys.FirebaseTenant]?.firebase_tenant_id;
    }

    return null;
  }

  public getMarketplaceTenantId(): string | null | undefined {
    const marketplace = this.getMarketplaceData();
    if (
      marketplace[MarketplaceKeys.FirebaseTenant] !== undefined &&
      marketplace[MarketplaceKeys.FirebaseTenant]?.id !== undefined
    ) {
      return marketplace[MarketplaceKeys.FirebaseTenant]?.id;
    }

    return null;
  }

  public getMarketplaceData(): DoodMarketplaceModel {
    return this.marketplaceSelector.marketplace;
  }

  private pickDomain(): {
    domain: string;
    envGuessedFromDomain: string | null;
    envFromParams: string | null;
    envFromLocalStorage: string | null;
    envGuessedFromLocalDomain: string | null;
  } {
    // From CLI env var
    // if (process.env.NG_APP_MARKETPLACE_DOMAIN) {
    //   return process.env.NG_APP_MARKETPLACE_DOMAIN;
    // }

    // Domain from GET query param
    const localDomain = localStorage.getItem(MarketplaceService.localStorageKey);
    const envGuessedFromLocalDomain = determineDomainEnvironmentFromDomain(localDomain ?? '');

    const envFromLocalStorage = localStorage.getItem('env');
    const urlSearchParams = new URLSearchParams(window.location.search);
    const envFromParams = urlSearchParams.get('env');
    if (urlSearchParams.has('marketplace_domain')) {
      const { domain, env: envGuessedFromDomain } = extractDomainFromUrl(
        urlSearchParams.get('marketplace_domain'),
      );
      return {
        domain,
        envGuessedFromDomain,
        envFromParams,
        envFromLocalStorage,
        envGuessedFromLocalDomain,
      };
    }

    // From local storage
    if (localDomain) {
      return {
        domain: localDomain,
        envGuessedFromDomain: null,
        envFromParams,
        envFromLocalStorage,
        envGuessedFromLocalDomain,
      };
    }

    if (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '10.0.2.2'
    ) {
      return {
        domain: 'dood.com',
        envGuessedFromDomain: null,
        envFromParams: null,
        envFromLocalStorage: null,
        envGuessedFromLocalDomain: null,
      };
    }

    // From hostname
    return {
      domain: window.location.hostname,
      envGuessedFromDomain: null,
      envFromParams: null,
      envFromLocalStorage: null,
      envGuessedFromLocalDomain: null,
    };
  }

  private saveDomainToLocalStorage(marketplaceDomain: string): void {
    localStorage.setItem(MarketplaceService.localStorageKey, marketplaceDomain);
  }

  private loadMarketplaceData(marketplace: DoodMarketplaceModel): void {
    if (!marketplace.id) {
      alert('Marketplace not found');
    }

    this.marketplaceDispatcher.updateMarketplace(marketplace);
  }

  private materializeMarketplaceRuntime$(
    marketplace: DoodMarketplaceModel,
  ): Observable<DoodMarketplaceModel> {
    if (!isContentBuilderV2Marketplace(marketplace)) {
      this.journeyParametersRuntime.clear();
      this.cb2ContextualRuntimeResolver.clear();
      return of(marketplace);
    }

    return this.marketplaceContentBuilderApiService
      .getCompiledRuntime$(marketplace.id, readContentBuilderSpecModeFromUrl(window.location.href))
      .pipe(
        map(runtime => {
          const resolvedBlocks = this.cb2ContextualRuntimeResolver.stageCompiledRuntime({
            blocks: runtime.blocks ?? [],
            paramValues: runtime.paramValues,
            paramCatalog: runtime.paramCatalog,
          });

          const merged: DoodMarketplaceModel = {
            ...marketplace,
            pages: runtime.pages as DoodMarketplaceModel['pages'],
            blocks: resolvedBlocks as DoodMarketplaceModel['blocks'],
            modals: runtime.modals as DoodMarketplaceModel['modals'],
            // CB2 themes publish colors as params (`ui.colors`); project them onto
            // user_interface so applyMarketplaceUserInterface sets --ui-color-*.
            user_interface: {
              ...(marketplace.user_interface ?? {}),
              ...(runtime.user_interface ?? {}),
            } as DoodMarketplaceModel['user_interface'],
          };

          return merged;
        }),
        catchError(() => {
          return of(marketplace);
        }),
      );
  }

  private loadBlocks(marketplace: DoodMarketplaceModel): void {
    this.contentDispatcher.fetchBlocksSuccess(marketplace.blocks ?? []);
  }

  private loadModals(marketplace: DoodMarketplaceModel): void {
    this.contentDispatcher.setModals(marketplace.modals ?? []);
  }

  private injectCustomTranslation(marketplace: DoodMarketplaceModel): void {
    if (!marketplace.user_interface || !('translations' in marketplace.user_interface)) {
      return;
    }

    for (const lang of Object.keys(marketplace.user_interface.translations)) {
      const translations = marketplace.user_interface.translations[lang];
      this.translate.setTranslation(lang, translations, true);
    }
  }

  private setAuthMode(marketplace: DoodMarketplaceModel): void {
    const authMode = resolveAuthMode(marketplace);
    if (authMode) {
      this.marketplaceDispatcher.updateMarketplace({
        auth_mode: authMode,
      });
    }
  }

  private dispatchTrackEvent(marketplace: DoodMarketplaceModel): void {
    this.eventTrackerService.dispatch(EventTrackerService.EventMarketplaceViewed, marketplace);
  }

  private setFirebaseTenantId(marketplace: DoodMarketplaceModel) {
    let tenantId = marketplace.firebase_tenant?.firebase_tenant_id;
    this.auth.tenantId = tenantId ?? null;
  }
}
